import { DOCUMENT } from "@angular/common";
import { HttpBackend, HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { lastValueFrom, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { loadLanguage } from "src/i18n";
import { lookupLocale } from "src/i18n/locale-lookup";
import { BUILTIN_LOCALES } from "src/i18n/locale-mappings";

import { ConfigurationState } from "../configuration/state/configuration.state";

import { AddDynamicAsset } from "./dynamic-asset.actions";

import type { DynamicAsset } from "./dynamic-asset";

const ONSITE_PREFIX = "onsite";
const TRANSLATIONS_FALLBACK = "assets/translations";

const ASSETS: DynamicAsset[] = [
	{
		name: "theme",
		path: {
			wanted: `${ONSITE_PREFIX}/theme.css`,
			fallback: "theme.css",
		},
		type: "style",
	},
	{
		name: "customjs",
		path: {
			wanted: `${ONSITE_PREFIX}/custom.js`,
		},
		type: "script",
	},
	{
		name: "logo",
		path: {
			wanted: `${ONSITE_PREFIX}/logo.png`,
			fallback: "assets/images/logo.png",
		},
		type: "file",
		addToState: true,
	},
];

@Injectable({
	providedIn: "root",
})
export class DynamicAssetInitializerService {
	private readonly _httpClient: HttpClient;

	constructor(
		private store: Store,
		http: HttpBackend,
		@Inject(DOCUMENT) private document: Document,
	) {
		this._httpClient = new HttpClient(http);
	}

	public async init(): Promise<void> {
		const rootConfig = this.store.selectSnapshot(ConfigurationState.rootConfiguration);
		const dsConfig = this.store.selectSnapshot(ConfigurationState.dsConfiguration);

		for (const asset of ASSETS) {
			let relevantPath: string | undefined;
			if (rootConfig?.roomsUrl) {
				relevantPath = await this.determineRelevantPath(
					`${rootConfig.roomsUrl}/Content/${asset.path.wanted}`,
					asset.path.fallback,
				);
			} else {
				relevantPath = asset.path.fallback;
			}

			if (!relevantPath) {
				continue;
			}

			switch (asset.type) {
				case "style":
					this.attachStylesheet(relevantPath);
					break;
				case "script":
					this.attachScript(relevantPath);
					break;
			}

			if (asset.addToState) {
				await this.store.dispatch(new AddDynamicAsset(asset.name, relevantPath)).toPromise();
			}
		}

		if (dsConfig?.logoUrl) {
			await this.store.dispatch(new AddDynamicAsset("logo", dsConfig?.logoUrl)).toPromise();
		}

		const isoLanguageName = dsConfig?.locale ? dsConfig.locale : "";
		let translationsPath = TRANSLATIONS_FALLBACK;
		if (rootConfig?.roomsUrl) {
			const dynamicTranslationsRoot = `${rootConfig.roomsUrl}/Content/${ONSITE_PREFIX}/translations`;

			const locale = lookupLocale(isoLanguageName, BUILTIN_LOCALES);
			const roomsHasTranslations = await this.fileExists(
				`${dynamicTranslationsRoot}/${locale}.json`,
			);
			if (roomsHasTranslations) {
				translationsPath = dynamicTranslationsRoot;
			}
		}

		await loadLanguage(isoLanguageName, translationsPath);
	}

	private attachStylesheet(href: string): void {
		const customCssLink = this.document.createElement("link");
		customCssLink.rel = "stylesheet";
		customCssLink.type = "text/css";
		customCssLink.href = this.buildUrl(href);
		this.document.head.appendChild(customCssLink);
	}

	private attachScript(href: string): void {
		const customScriptLink = this.document.createElement("script");
		customScriptLink.src = this.buildUrl(href);
		this.document.body.appendChild(customScriptLink);
	}

	private async determineRelevantPath(dynamicUrl: string, fallback?: string): Promise<string> {
		if (!fallback) {
			return dynamicUrl;
		}

		const exists = await this.fileExists(dynamicUrl);
		return exists ? dynamicUrl : fallback;
	}

	private async fileExists(url: string): Promise<boolean | undefined> {
		return lastValueFrom(
			this._httpClient
				.get(url, {
					params: {
						ignoreLocalFiles: `${true}`,
						cacheBuster: `${Date.now()}`,
					},
					observe: "response",
					responseType: "text",
				})
				.pipe(
					map(() => true),
					catchError(() => of(false)),
				),
		);
	}

	private buildUrl(url: string): string {
		return `${url}?ignoreLocalFiles=true&cacheBuster=${Date.now()}`;
	}
}
